/*
 * Byte-identical in Cortex and Quantum.
 *
 * A PLATFORM'S ROLE IS NEVER ITS ALLOCATION.
 *
 * A platform card carries STRATEGY fields — role, funnel purpose, targeting,
 * creative, measurement — beside FINANCIAL fields — budget, share, allocation
 * percent. The strategy page used to fill the Platform Mix role pill with
 * `funnelPurpose || shareLabel || "Active"`, so a card that reached the page
 * without a funnel purpose wore its share as its role: Campaign 55 (OutKick,
 * General) showed CTV as "36%" beside a blank write-up, because the General
 * template filter (micrositeGeneralTemplate.js) had rightly removed the film
 * copy the backend served it and CTV had nothing else.
 *
 * The backend now serves category-safe, complete platform strategy on a stamped
 * artifact (presentation contract v2, categorySafePlatformStrategy;
 * backend utils/micrositePlatformStrategy.js). This module is the page's side of
 * the same contract, on every artifact:
 *   - a strategy slot reads only its own strategy field; a value that is only an
 *     amount, a share or a rate ("36%", "$14,400") is not strategy;
 *   - a missing field reads a neutral state ("Planned", the page's role
 *     fallback), never another field's value and never invented copy;
 *   - the card, its platform and its allocation always render;
 *   - the missing fields are named on the card (data-missing-strategy-fields).
 * A card that arrives complete renders exactly as it did.
 */

export const PLATFORM_STRATEGY_FIELDS = Object.freeze([
  "role",
  "funnelPurpose",
  "targetingLogic",
  "creativeApproach",
  "measurementLogic",
]);

// Money and share fields a platform card carries. None of them is strategy.
export const PLATFORM_FINANCIAL_FIELDS = Object.freeze([
  "budget",
  "budgetLabel",
  "share",
  "shareLabel",
  "percent",
  "allocation",
  "allocationLabel",
  "allocationPercent",
  "canonicalAllocationLabel",
  "spend",
  "cpm",
  "impressionShare",
]);

// The role pill's neutral state when a card has no funnel purpose: the word the
// detailed platform card already uses for a funnel purpose not yet set.
export const PLATFORM_ROLE_PILL_FALLBACK = "Planned";

// A value that is only an amount, a percentage or a rate: "36%", "$14,400",
// "$18 CPM", "14.4k".
const FINANCIAL_VALUE_PATTERN =
  /^\s*(?:[$€£]\s*)?\d[\d,]*(?:\.\d+)?\s*(?:%|[km]|(?:cpm|cpc|cpa|cpv|cpcv)\b)?\s*(?:of\s+[\w\s]+)?$/i;

export const isFinancialStrategyValue = (value) =>
  typeof value === "string" && value.trim() !== "" && FINANCIAL_VALUE_PATTERN.test(value);

// A strategy field's value, or "" when the card has none (blank, not text, or
// only a financial figure).
export const resolvePlatformStrategyValue = (card, field) => {
  const value = card?.[field];
  if (typeof value !== "string" || value.trim() === "" || isFinancialStrategyValue(value)) return "";
  return value;
};

export const describePlatformStrategyCompleteness = (card) => {
  const missingStrategyFields = PLATFORM_STRATEGY_FIELDS.filter(
    (field) => !resolvePlatformStrategyValue(card, field)
  );
  return { complete: missingStrategyFields.length === 0, missingStrategyFields };
};

// The Platform Mix role pill: the card's funnel purpose, or the neutral state.
export const resolvePlatformRolePillLabel = (card) =>
  resolvePlatformStrategyValue(card, "funnelPurpose") || PLATFORM_ROLE_PILL_FALLBACK;

// Props for a platform card's element: names the strategy fields the card
// arrived without, so tests and anyone inspecting the page can see them. A
// complete card gets no attribute at all.
export const platformStrategyCompletenessProps = (card) => {
  const { missingStrategyFields } = describePlatformStrategyCompleteness(card);
  return missingStrategyFields.length
    ? { "data-missing-strategy-fields": missingStrategyFields.join(" ") }
    : {};
};
